import { useMemo } from "react";
import Supercluster from "supercluster";
import type { Vehicle } from "@/types";

// Agrupamento de marcadores pro mapa (deck.gl) — reimplementado 2026-09-03
// depois de sumir na migração pra deck.gl (não precisava mais agrupar pra
// manter o DOM baixo, já que deck.gl desenha tudo num canvas só). O
// problema que sobrou não era de performance/DOM, era visual: com zoom bem
// aberto (frota inteira do Brasil, ou qualquer polo com centenas de
// veículos a poucos km um do outro), centenas de ícones de 44px caem
// literalmente no mesmo pixel e viram uma mancha preta sólida (bug real
// relatado no PWA, print 2026-09-03). Supercluster resolve isso: agrupa
// pontos próximos (na projeção de tela, por zoom) numa "bolha" com
// contagem, só mostra o ícone individual quando dá zoom suficiente pra
// não sobrepor.

export interface VehicleClusterPoint {
  type: "vehicle";
  vehicle: Vehicle;
  lat: number;
  lng: number;
}

export interface VehicleClusterBubble {
  type: "cluster";
  clusterId: number;
  lat: number;
  lng: number;
  count: number;
}

export type VehicleClusterItem = VehicleClusterPoint | VehicleClusterBubble;

export interface MapBoundsBox {
  west: number;
  south: number;
  east: number;
  north: number;
}

// radius em pixels de tela (não metros) — mesma unidade que o supercluster
// espera; 60 ~ um pouco mais que o diâmetro do ícone (44px) + folga, pra
// nunca deixar dois ícones vizinhos se tocando virarem uma mancha.
const CLUSTER_RADIUS_PX = 60;
const CLUSTER_MAX_ZOOM = 16; // acima disso sempre mostra individual, nunca agrupa
// Com `bubbleSingles`, veículo sozinho também vira bolha ("1") abaixo deste
// zoom — assim quem tem poucos veículos (ex.: 1) vê o Cluster funcionar.
const SINGLE_BUBBLE_MAX_ZOOM = 15;
const SINGLE_CLUSTER_ID = -1;

// Raio em pixels da BOLHA desenhada pro cluster (conceito diferente de
// CLUSTER_RADIUS_PX acima, que é o raio de AGRUPAMENTO do supercluster) —
// função única usada pelos dois mapas (desktop e PWA) pra nunca desafinar
// um sem o outro. Ajustado 2026-09-03 pra baixo (pedido: "os cluster estão
// muito grandes") — cresce bem devagar com a contagem, sempre menor que
// um círculo folgado ao redor do ícone de 44px de um veículo individual.
export function clusterBubbleRadiusPx(count: number): number {
  return 10 + Math.min(12, Math.sqrt(count) * 1.6);
}

export function useVehicleClusters(
  vehicles: Vehicle[],
  bounds: MapBoundsBox | null,
  zoom: number,
  // Veículo que deve SEMPRE aparecer individual, nunca escondido por
  // bounds/cluster — usado pelo veículo em foco (ver CameraController).
  // Bug relatado 2026-09-09: clicar "Acessar rastreamento" no Início leva
  // pro mapa e foca a câmera nesse veículo, mas a câmera ainda está
  // animando (pan/zoom) quando o primeiro "idle" já reporta bounds —
  // qualquer atraso na câmera terminar de chegar deixava o veículo fora do
  // bbox reportado e, com "Agrupar veículos" ligado (padrão), ele
  // literalmente sumia do desenho até a câmera assentar. Forçar esse ID a
  // sempre aparecer garante que o veículo em foco nunca depende de timing
  // de câmera pra ser visível, independente de qualquer race de bounds.
  alwaysIncludeId?: string | null,
  bubbleSingles = false,
): { items: VehicleClusterItem[]; expansionZoomFor: (clusterId: number) => number } {
  const index = useMemo(() => {
    const sc = new Supercluster<{ vehicleId: string }>({
      radius: CLUSTER_RADIUS_PX,
      // extent 256 = tile do Google Maps; o padrão (512) fazia o raio valer
      // só metade em pixels reais de tela.
      extent: 256,
      maxZoom: CLUSTER_MAX_ZOOM,
    });
    sc.load(
      vehicles.map((v) => ({
        type: "Feature",
        properties: { vehicleId: v.id },
        geometry: { type: "Point", coordinates: [v.lng, v.lat] },
      })),
    );
    return sc;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicles]);

  const vehicleById = useMemo(() => new Map(vehicles.map((v) => [v.id, v] as const)), [vehicles]);

  const items = useMemo((): VehicleClusterItem[] => {
    // Sem bounds ainda (mapa não terminou de montar) — cai pro comportamento
    // anterior (tudo individual) em vez de esconder a frota inteira.
    if (!bounds) {
      return vehicles.map((v) => ({ type: "vehicle", vehicle: v, lat: v.lat, lng: v.lng }));
    }
    const z = Math.round(Math.min(Math.max(zoom, 0), 20));
    const features = index.getClusters([bounds.west, bounds.south, bounds.east, bounds.north], z);
    const out: VehicleClusterItem[] = [];
    for (const f of features) {
      const [lng, lat] = f.geometry.coordinates;
      // "in" (não acesso direto) — a outra metade da união (ponto
      // individual) nem tem essa chave, então TS só estreita o tipo certo
      // com esse formato de checagem.
      if ("cluster" in f.properties) {
        out.push({
          type: "cluster",
          clusterId: f.properties.cluster_id,
          lat,
          lng,
          count: f.properties.point_count,
        });
      } else {
        const vehicle = vehicleById.get(f.properties.vehicleId);
        if (vehicle) {
          if (bubbleSingles && z < SINGLE_BUBBLE_MAX_ZOOM && vehicle.id !== alwaysIncludeId) {
            out.push({ type: "cluster", clusterId: SINGLE_CLUSTER_ID, lat, lng, count: 1 });
          } else {
            out.push({ type: "vehicle", vehicle, lat, lng });
          }
        }
      }
    }
    if (alwaysIncludeId) {
      const alreadyIndividual = out.some(
        (item) => item.type === "vehicle" && item.vehicle.id === alwaysIncludeId,
      );
      if (!alreadyIndividual) {
        const forced = vehicleById.get(alwaysIncludeId);
        if (forced) out.push({ type: "vehicle", vehicle: forced, lat: forced.lat, lng: forced.lng });
      }
    }
    return out;
  }, [index, bounds, zoom, vehicles, vehicleById, alwaysIncludeId, bubbleSingles]);

  const expansionZoomFor = (clusterId: number) => {
    if (clusterId === SINGLE_CLUSTER_ID) return CLUSTER_MAX_ZOOM;
    try {
      return index.getClusterExpansionZoom(clusterId);
    } catch {
      return Math.min((Math.round(zoom) || 0) + 2, CLUSTER_MAX_ZOOM);
    }
  };

  return { items, expansionZoomFor };
}
